import { createContext, useContext, useEffect, useMemo, useState } from "react";

const UnitsContext = createContext(null);
export const useUnits = () => useContext(UnitsContext);

const LS = "tk-units";
const load = () => {
  try { return JSON.parse(localStorage.getItem(LS)) || {}; } catch { return {}; }
};

// Toe can be expressed as an angle (deg) or as a linear distance (in / mm) across the wheel diameter.
const CFG = {
  deg: { step: 0.02, dec: 2, suf: "°" },
  in: { step: 0.01, dec: 2, suf: " in" },
  mm: { step: 0.5, dec: 1, suf: " mm" },
};

export function UnitsProvider({ children }) {
  const saved = load();
  const [toeUnit, setToeUnitState] = useState(saved.toeUnit || "deg");
  const [wheelDia, setWheelDiaState] = useState(saved.wheelDia || 17);

  useEffect(() => {
    localStorage.setItem(LS, JSON.stringify({ toeUnit, wheelDia }));
  }, [toeUnit, wheelDia]);

  const api = useMemo(() => {
    const cfg = CFG[toeUnit] || CFG.deg;
    const degToToe = (deg) => {
      if (deg === null || deg === undefined || deg === "" || Number.isNaN(Number(deg))) return null;
      const d = Number(deg);
      if (toeUnit === "deg") return d;
      const inches = wheelDia * Math.tan((d * Math.PI) / 180);
      return toeUnit === "mm" ? inches * 25.4 : inches;
    };
    const toeToDeg = (val) => {
      if (val === null || val === undefined || val === "" || Number.isNaN(Number(val))) return "";
      const v = Number(val);
      if (toeUnit === "deg") return v;
      const inches = toeUnit === "mm" ? v / 25.4 : v;
      return (Math.atan(inches / wheelDia) * 180) / Math.PI;
    };
    // number only (no suffix)
    const toeNum = (deg, sign = false) => {
      const n = degToToe(deg);
      if (n === null) return "--";
      return `${sign && n >= 0 ? "+" : ""}${n.toFixed(cfg.dec)}`;
    };
    // number + unit suffix
    const fmtToe = (deg, sign = true) => {
      const n = degToToe(deg);
      if (n === null) return "--";
      return `${toeNum(deg, sign)}${cfg.suf}`;
    };
    const fmtToeRange = (spec) => `${toeNum(spec.min)} … ${toeNum(spec.max)}${cfg.suf}`;
    // display string for a stored degree value, in the current unit (for inputs)
    const toeInputValue = (deg) => {
      if (deg === "" || deg === null || deg === undefined) return "";
      const n = degToToe(deg);
      return n === null ? "" : String(Math.round(n * 10 ** cfg.dec) / 10 ** cfg.dec);
    };
    return {
      toeUnit, setToeUnit: setToeUnitState,
      wheelDia, setWheelDia: (v) => setWheelDiaState(Number(v) || 17),
      toeStep: cfg.step, toeSuffix: cfg.suf, toeDecimals: cfg.dec,
      degToToe, toeToDeg, toeNum, fmtToe, fmtToeRange, toeInputValue,
    };
  }, [toeUnit, wheelDia]);

  return <UnitsContext.Provider value={api}>{children}</UnitsContext.Provider>;
}
